import { ArrowLeft, Info } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { useNavigate, useParams } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const MealScore = () => {
  const navigate = useNavigate();
  const { mealType } = useParams();

  const scoreData: Record<
    string,
    {
      name: string;
      overallScore: number;
      factors: { name: string; score: number; color: string }[];
    }
  > = {
    breakfast: {
      name: "Breakfast",
      overallScore: 82,
      factors: [
        { name: "Carb-Fiber Ratio", score: 78, color: "hsl(var(--chart-1))" },
        { name: "Fat Quality", score: 85, color: "hsl(var(--chart-2))" },
        { name: "Sugar Load", score: 72, color: "hsl(var(--chart-3))" },
        { name: "Protein-Fiber", score: 88, color: "hsl(var(--chart-4))" },
      ],
    },
    lunch: {
      name: "Lunch",
      overallScore: 91,
      factors: [
        { name: "Carb-Fiber Ratio", score: 90, color: "hsl(var(--chart-1))" },
        { name: "Fat Quality", score: 92, color: "hsl(var(--chart-2))" },
        { name: "Sugar Load", score: 95, color: "hsl(var(--chart-3))" },
        { name: "Protein-Fiber", score: 86, color: "hsl(var(--chart-4))" },
      ],
    },
    snack: {
      name: "Snack",
      overallScore: 65,
      factors: [
        { name: "Carb-Fiber Ratio", score: 68, color: "hsl(var(--chart-1))" },
        { name: "Fat Quality", score: 80, color: "hsl(var(--chart-2))" },
        { name: "Sugar Load", score: 45, color: "hsl(var(--chart-3))" },
        { name: "Protein-Fiber", score: 58, color: "hsl(var(--chart-4))" },
      ],
    },
    dinner: {
      name: "Dinner",
      overallScore: 88,
      factors: [
        { name: "Carb-Fiber Ratio", score: 85, color: "hsl(var(--chart-1))" },
        { name: "Fat Quality", score: 95, color: "hsl(var(--chart-2))" },
        { name: "Sugar Load", score: 82, color: "hsl(var(--chart-3))" },
        { name: "Protein-Fiber", score: 88, color: "hsl(var(--chart-4))" },
      ],
    },
  };

  const meal = scoreData[mealType || "breakfast"];

  if (!meal) {
    navigate("/meals");
    return null;
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) {
      return "text-emerald-500";
    }
    if (score >= 60) {
      return "text-amber-500";
    }
    return "text-red-500";
  };

  const getScoreStroke = (score: number) => {
    if (score >= 80) {
      return "text-emerald-500";
    }
    if (score >= 60) {
      return "text-amber-500";
    }
    return "text-red-500";
  };

  const getLabel = (score: number) => {
    if (score >= 80) {
      return "Excellent";
    }
    if (score >= 60) {
      return "Good";
    }
    if (score >= 40) {
      return "Moderate";
    }
    return "Poor";
  };

  const pieData = meal.factors.map(f => ({
    name: f.name,
    value: f.score,
    color: f.color,
  }));

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 pt-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold text-foreground">
            {meal.name} Score
          </h1>
        </div>

        {/* Score Circle */}
        <div className="flex flex-col items-center py-6">
          <div className="relative h-32 w-32">
            <svg className="h-32 w-32 -rotate-90 transform">
              <circle
                cx="64"
                cy="64"
                r="54"
                stroke="currentColor"
                strokeWidth="10"
                fill="none"
                className="text-muted/20"
              />
              <circle
                cx="64"
                cy="64"
                r="54"
                stroke="currentColor"
                strokeWidth="10"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 54}`}
                strokeDashoffset={`${2 * Math.PI * 54 * (1 - meal.overallScore / 100)}`}
                className={`${getScoreStroke(meal.overallScore)} transition-all duration-700`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span
                className={`text-4xl font-bold ${getScoreColor(meal.overallScore)}`}
              >
                {meal.overallScore}
              </span>
              <span className="text-xs text-muted-foreground">
                {getLabel(meal.overallScore)}
              </span>
            </div>
          </div>
        </div>

        {/* Donut Chart */}
        <div className="px-4">
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={4}
                  dataKey="value"
                  stroke="none"
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `${value}/100`,
                    name,
                  ]}
                  contentStyle={{
                    borderRadius: "12px",
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    fontSize: "12px",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="mt-2 grid grid-cols-2 gap-3">
            {meal.factors.map((factor, i) => (
              <div key={i} className="flex items-center gap-2">
                <div
                  className="h-3 w-3 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: factor.color }}
                />
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-foreground">
                    {factor.name}
                  </p>
                  <p
                    className={`text-sm font-bold ${getScoreColor(factor.score)}`}
                  >
                    {factor.score}
                    <span className="text-[10px] font-normal text-muted-foreground">
                      /100
                    </span>
                  </p>
                </div>
              </div>
            ))}
            {/* What is Meal Score */}
            <Card className="mt-6 rounded-2xl border-0 p-4 shadow-sm">
              <div className="mb-2 flex items-center gap-2">
                <Info className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">
                  What is Meal Score?
                </h3>
              </div>
              <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
                Meal Score rates each meal from 0–100 based on how nutritionally
                balanced it is. It evaluates four key factors:
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <div
                    className="mt-1 h-2 w-2 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: "hsl(var(--chart-1))" }}
                  />
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">
                      Carb-Fiber Ratio
                    </span>{" "}
                    — Measures how well fiber offsets carbohydrate intake. Lower
                    ratios are better.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div
                    className="mt-1 h-2 w-2 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: "hsl(var(--chart-2))" }}
                  />
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">
                      Fat Quality
                    </span>{" "}
                    — Evaluates the proportion of healthy unsaturated fats vs.
                    saturated fats.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div
                    className="mt-1 h-2 w-2 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: "hsl(var(--chart-3))" }}
                  />
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">
                      Sugar Load
                    </span>{" "}
                    — Tracks total sugar content and its glycemic impact on
                    blood glucose.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div
                    className="mt-1 h-2 w-2 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: "hsl(var(--chart-4))" }}
                  />
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">
                      Protein-Fiber
                    </span>{" "}
                    — Checks if protein and fiber are adequate for satiety and
                    digestion.
                  </p>
                </div>
              </div>
            </Card>

            {/* Score Ranges */}
            <div className="mb-2 mt-4 grid grid-cols-4 gap-2">
              <div className="text-center">
                <div className="text-sm font-bold text-emerald-500">80–100</div>
                <div className="text-[10px] text-muted-foreground">
                  Excellent
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-bold text-amber-500">60–79</div>
                <div className="text-[10px] text-muted-foreground">Good</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-bold text-orange-500">40–59</div>
                <div className="text-[10px] text-muted-foreground">
                  Moderate
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-bold text-red-500">0–39</div>
                <div className="text-[10px] text-muted-foreground">Poor</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealScore;
